import { lazy } from "react";
import * as XLSX from "xlsx";

export const sections = [
  {
    id: "beneficiaryData",
    title: "Dados do Beneficiário",
    component: lazy(() => import("./Beneficiary")),
  },
  {
    id: "economic",
    title: "Unidade Económica",
    component: lazy(() => import("./EconomicUnit")),
  },
  {
    id: "marketAnalysis",
    title: "Análise Mercado",
    component: lazy(() => import("./MarketAnalysis")),
  },
  {
    id: "balance",
    title: "Balanço e DR",
    component: lazy(() => import("./BalanceDR")),
  },
  {
    id: "project",
    title: "Dados do Projeto",
    component: lazy(() => import("./ProjectData")),
  },
  {
    id: "typologies",
    title: "Tipologias",
    component: lazy(() => import("./Typologies")),
  },
  {
    id: "activities",
    title: "Lista de Atividades",
    component: lazy(() => import("./Activities")),
  },
  {
    id: "costs",
    title: "Custos",
    component: lazy(() => import("./Costs")),
  },
  {
    id: "salaryCosts",
    title: "Custos Salariais",
    component: lazy(() => import("./SalaryCosts")),
  },
  {
    id: "operationDescription",
    title: "Descrição da Operação",
    component: lazy(() => import("./OperationDescription")),
  },
  {
    id: "industry40",
    title: "Industria 4.0",
    component: lazy(() => import("./Industry40")),
  },
  {
    id: "climateTransaction",
    title: "Transição Climática",
    component: lazy(() => import("./ClimateTransition")),
  },
  {
    id: "financing",
    title: "Financiamento",
    component: lazy(() => import("./Financing")),
  },
  {
    id: "thematic",
    title: "Enquadramento Temático",
    component: lazy(() => import("./Thematic")),
  },
  {
    id: "selectionCriteria",
    title: "Critérios de Seleção",
    component: lazy(() => import("./SelectionCriteria")),
  },
  {
    id: "declarations",
    title: "Declarações",
    component: lazy(() => import("./Declarations")),
  },
];

export const importFiles = {
  Mérito: (workbook, formData, jsonData, handleChange, setToast) => {
    const balanceSheet = workbook.Sheets[`Balanço`];
    const balaceData = XLSX.utils.sheet_to_json(balanceSheet, {
      range: "C9:L71",
      blankrows: true,
      defval: "",
      skipHidden: false,
      header: ["22", "23", "24", "25", "26", "27", "28", "29", "30", "31"],
    });

    const drSheet = workbook.Sheets[`DR`];
    const drData = XLSX.utils.sheet_to_json(drSheet, {
      range: "C11:L40",
      blankrows: true,
      defval: "",
      skipHidden: false,
      header: ["22", "23", "24", "25", "26", "27", "28", "29", "30", "31"],
    });

    const financeSheet = workbook.Sheets[`Financiamento Inov`];
    const yearCell = financeSheet["D3"]?.v;
    const is2024 = yearCell == 2024;

    const range = is2024 ? "D4:H20" : "D4:G20";
    const header = is2024
      ? ["24", "25", "26", "27", "28"]
      : ["25", "26", "27", "28"];

    const financingData = XLSX.utils.sheet_to_json(financeSheet, {
      range: range,
      blankrows: true,
      defval: "",
      skipHidden: false,
      header: header,
    });

    let id = 1;
    const costsSheet = workbook.Sheets["Quadro de Investimentos Inov"];
    const costsData = XLSX.utils
      .sheet_to_json(costsSheet, {
        range: "B5:J79",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "designation",
          "act",
          "loc",
          "u1",
          "u2",
          "acquisitionDate",
          "u4",
          "category",
          "eligibleCost",
        ],
      })
      .map((cost) => {
        if (!cost.designation || cost.elegibleCost <= 0) return;
        const activityLabel = splitOnce(cost.act, "-");
        const locationLabel = splitOnce(cost.loc, "-");

        const category = jsonData["CostsCategory"].find(
          (cat) => cat.label === cost.category
        )?.value;
        const activity =
          String(
            formData["projectActivities"]?.find(
              (act) => act.designation === activityLabel
            )?.id
          ) ?? "";
        const location =
          String(
            formData["projectLocation"]?.find(
              (loc) => loc.address === locationLabel
            )?.id
          ) ?? "";

        const acquisitionDate =
          excelSerialToJSDate(cost.acquisitionDate)
            ?.toISOString()
            .slice(0, 7) ?? "";

        return {
          id: id++,
          designation: cost.designation,
          category,
          activity,
          institute: location,
          acquisitionDate,
          eligibleCost: cost.eligibleCost,
        };
      })
      .filter(Boolean);

    const meritoSheet = workbook.Sheets["Mérito Inov"];
    const activityPerMarketData = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "L38:R73",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "service",
          "new",
          "country",
          "preQuantity",
          "preValue",
          "postQuantity",
          "postValue",
        ],
      })
      .map((act) => {
        if (!act.country) return;

        const service = jsonData["Services"]?.find(
          (service) => service.label === act.service
        )?.value;
        const country = jsonData["Countries"]?.find(
          (country) => country.label === act.country
        )?.value;
        const new_ = act.new === "Existente" ? "E" : "N";

        return { ...act, type: "01", service, new: new_, country };
      })
      .filter(Boolean);

    const indirectExportationData = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "U30:AC42",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "nif",
          "label",
          "preSells",
          "preVolInter",
          "preVolTotal",
          "u1",
          "postSells",
          "postVolInter",
          "postVolTotal",
        ],
      })
      .map((exp) => {
        if (!exp.label) return;

        delete exp.u1;

        return exp;
      })
      .filter(Boolean);

    const subImportsData = XLSX.utils
      .sheet_to_json(meritoSheet, {
        range: "AE30:AL42",
        blankrows: true,
        defval: "",
        skipHidden: false,
        header: [
          "nif",
          "label",
          "u1",
          "preImports",
          "preBuys",
          "postImports",
          "postSells",
          "postBuys",
          "subsImports",
        ],
      })
      .map((exp) => {
        if (!exp.label) return;

        delete exp.u1;

        return exp;
      })
      .filter(Boolean);

    handleChange("balance", balaceData);
    handleChange("dr", drData);
    handleChange("financing", financingData);
    handleChange("projectCosts", costsData);
    handleChange("activityPerMarket", activityPerMarketData);
    handleChange("indirectExportation", indirectExportationData);
    handleChange("subImports", subImportsData);

    setToast({
      title: "Mérito",
      message:
        "Análise de Mercado, Contas e Financiamento foram carregadas com sucesso!",
      type: "success",
    });
  },
};

function splitOnce(str, delimiter) {
  if (!str) return "";

  const index = str.indexOf(delimiter);
  if (index === -1) return "";
  return str.slice(index + delimiter.length).trim();
}

function excelSerialToJSDate(xlSerial) {
  const out = new Date(
    -2209075200000 + (xlSerial - (xlSerial < 61 ? 0 : 1)) * 86400000
  );
  if (isNaN(out.getTime())) {
    return null; // Invalid date
  }
  return out;
}
